/**
 * Approval gates (MD-02 §7), as the API returns them.
 *
 * A gate binds an approver plan to an attachment point on an award — an event,
 * optionally narrowed to one stage. With no gate configured the attachment
 * executes directly; with one, the whole decision parks until the plan resolves.
 */

/** The events an award can gate. Mirrors the server's GATEABLE_EVENTS. */
export const GATEABLE_EVENTS = [
  'stage.open',
  'stage.close',
  'screening.pass',
  'stage.decide',
  'winners.decide',
  'application.disqualify',
] as const;
export type GateableEvent = (typeof GATEABLE_EVENTS)[number];

/** How a step decides: any one approver, all of them, or a named quorum. */
export const STEP_RULES = ['ANY', 'ALL', 'QUORUM'] as const;
export type StepRule = (typeof STEP_RULES)[number];

export interface ApproverStep {
  /** Approvers on this step; they act in parallel. */
  approverIds: string[];
  rule: StepRule;
  /** Required APPROVE count when the rule is QUORUM. */
  quorum?: number;
  /** Step deadline in hours — drives the SLA reminders and escalation. */
  slaHours?: number;
  /** Joins the step on escalation at 100% of the SLA; the original stays on it. */
  fallbackApproverId?: string;
}

export interface ApproverPlan {
  steps: ApproverStep[];
}

/** GET /awards/:awardId/gates — one configured gate. */
export interface GateConfig {
  id: string;
  tenantId: string;
  awardId: string;
  event: string;
  /** Null for an award-level attachment. */
  stageId: string | null;
  plan: ApproverPlan;
  label: string | null;
}

/**
 * GET /approvals/gates/mine — a gate I approve on, with the names it is picked
 * by: the award, and the round as the published pipeline names it (null for a
 * whole-award gate or an unpublished award).
 */
export interface MyGate extends GateConfig {
  awardName: string | null;
  stageName: PackStageName | null;
}

export interface ConfigureGateInput {
  event: string;
  stageId?: string | null;
  plan: ApproverPlan;
  label?: string;
}

/** GET /approvals/queue — one item awaiting my decision. */
export interface QueueItem {
  instanceId: string;
  awardId: string;
  event: string;
  stageId: string | null;
  /** Which step of the plan is waiting on me. */
  stepIndex: number;
  /** Opaque reference to the decision; the readable content is the pack. */
  packRef: string;
}

/** One decision recorded on a gate instance. */
export interface StepDecisionRow {
  stepIndex: number;
  approverId: string;
  /** APPROVE | REJECT | CHANGES. */
  decision: string;
  comment: string | null;
  /**
   * The planned approver this was made FOR, when a delegate acted; null on a
   * direct decision. A register that cannot tell the two apart is missing the
   * fact most worth recording about a delegated decision.
   */
  onBehalfOf: string | null;
  at: string;
}

/** GET /awards/:awardId/gates/instances — the register view of an instance. */
export interface GateInstance {
  id: string;
  awardId: string;
  event: string;
  stageId: string | null;
  /** PENDING | APPROVED | REJECTED | CHANGES. */
  status: string;
  currentStep: number;
  payloadHash: string;
  packRef: string;
  /**
   * The plan AS IT STOOD when the instance opened, not the gate's current one —
   * so re-configuring a gate cannot rewrite the history of a decision already
   * made, and the register can show what was REQUIRED, not only what was done.
   */
  plan: ApproverPlan;
  createdAt: string;
  resolvedAt: string | null;
  stepStartedAt: string;
  decisions: StepDecisionRow[];
}

/** One application inside a pack, and what the decision would do to it. */
export interface PackApplication {
  applicationId: string;
  /** Null when the application has no profile — never a placeholder name. */
  applicantName: string | null;
  /** SHORTLISTED | PLACED | NOT_SELECTED. */
  outcome: string;
  /** Placements awarded (winners.decide only); empty otherwise. */
  placementIds: string[];
}

/**
 * Where a parked decision stands. PARKED waits on the approvers; a large one
 * (#132) and every #46 request pass through PARKING while they are prepared —
 * a verdict may already land there — then EXECUTING once approved, REVERTING
 * once sent back, and STALE when the approved action could no longer run.
 * Only PARKING and PARKED are open for a decision.
 */
export const DECISION_PACK_STATUSES = ['PARKING', 'PARKED', 'EXECUTING', 'EXECUTED', 'REVERTING', 'REJECTED', 'STALE'] as const;
export type DecisionPackStatus = (typeof DECISION_PACK_STATUSES)[number];

/** Which pack shape a parked decision reads as (#46 spec §9). Mirrors the server's DecisionPackKind. */
export type DecisionPackKind = 'DECISION' | 'ROUND' | 'ENTRIES';

/** A round's name as the frozen pipeline gives it. */
export interface PackStageName {
  en: string;
  rw?: string;
}

/** What approving a round request does (spec §9). Mirrors the server's ConsequenceCode. */
export const CONSEQUENCE_CODES = [
  'CLOSES_APPLICATIONS',
  'CLOSES_KNOWLEDGE_CHECK',
  'ADMITS_ENTRIES',
  'STOPS_ROUND_WORK',
  'ENABLES_DECIDE',
] as const;
export type ConsequenceCode = (typeof CONSEQUENCE_CODES)[number];

export interface PackConsequence {
  code: ConsequenceCode;
  /** How many entries it touches, when the pack could count them. */
  count?: number;
}

/** Where one entry of an ENTRIES pack stands. Mirrors the server's ENTRY_OUTCOMES. */
export const ENTRY_OUTCOMES = ['PENDING', 'EXECUTED', 'STALE', 'RETURNED'] as const;
export type EntryOutcome = (typeof ENTRY_OUTCOMES)[number];

/** The approver's verdict, or STALE when the approved action could not run. */
export type SignoffOutcome = 'APPROVED' | 'REJECTED' | 'CHANGES' | 'STALE';

/**
 * Why an approved request (or one entry of it) could not be applied: the copy
 * keys under `signoff.staleReason`. The server's final refusal codes, the
 * `SCREENING_CHECKLIST_*` family collapsed to one, the stale-proposal marker,
 * and UNKNOWN for anything newer than this list.
 */
export const STALE_REASON_CODES = [
  'STAGE_NOT_OPEN',
  'STAGE_NOT_REOPENABLE',
  'PREVIOUS_STAGE_NOT_DECIDED',
  'PRE_ASSESSMENT_WOULD_CLOSE',
  'WOULD_CLOSE_APPLICATIONS',
  'PASSES_AWAITING_SIGNOFF',
  'INVALID_TRANSITION',
  'JUSTIFICATION_REQUIRED',
  'NO_EVALUATION_STAGE',
  'APPLICATION_NOT_FOUND',
  'AWARD_NOT_FOUND',
  'NO_PIPELINE',
  'STAGE_NOT_FOUND',
  'ALREADY_DONE',
  'SCREENING_CHECKLIST',
  'stale_proposal',
  // Not a server code: the register's reason when the summary reports the
  // request's only entry was withdrawn, which the server records as a plain
  // INVALID_TRANSITION.
  'WITHDRAWN',
  'UNKNOWN',
] as const;
export type StaleReasonCode = (typeof STALE_REASON_CODES)[number];

/** The copy key for a raw stale reason: one checklist family, and UNKNOWN for codes this build does not know. */
export function staleReasonCode(raw: string | null | undefined): StaleReasonCode {
  if (!raw) return 'UNKNOWN';
  if (raw.startsWith('SCREENING_CHECKLIST')) return 'SCREENING_CHECKLIST';
  return (STALE_REASON_CODES as readonly string[]).includes(raw) ? (raw as StaleReasonCode) : 'UNKNOWN';
}

/** The verdict once given; null on the pack while it waits. */
export interface PackResolution {
  outcome: SignoffOutcome;
  decidedById: string | null;
  comment: string | null;
  staleReason?: string | null;
  counts?: { executed: number; stale: number };
}

/**
 * What every pack carries. Decide and winners keep `applications` and `counts`
 * exactly as before; the #46 kinds carry them empty.
 */
interface DecisionPackBase {
  kind: DecisionPackKind;
  instanceId: string;
  awardId: string;
  awardName: string | null;
  event: string;
  stageId: string | null;
  /** The parked round's name; null with no round or an unreadable pipeline. */
  stageName: PackStageName | null;
  status: DecisionPackStatus;
  /** The request came from the schedule, not a person. */
  requestedBySchedule: boolean;
  /** The admin whose decision this is; it executes on their behalf. Null for the schedule. */
  requestedBy: string | null;
  requestedById: string;
  requestedAt: string;
  /** The verdict; null until the request is finished (EXECUTED, REJECTED or STALE). */
  resolution: PackResolution | null;
  /**
   * Approved and still being applied (e.g. a round close waiting for an
   * admission run to end): the decision is made, so nothing is left to decide.
   */
  releasing: boolean;
  applications: PackApplication[];
  counts: { selected: number; notSelected: number };
}

/** stage.decide / winners.decide: the applications it disposes of. */
export interface DecisionDecisionPack extends DecisionPackBase {
  kind: 'DECISION';
}

/** The round a ROUND pack asks to open or close, as it stands now. */
export interface PackRound {
  stageId: string;
  stageName: PackStageName | null;
  stageType: string | null;
  /** SCHEDULED, OPEN, ...; null before the round was materialised. */
  statusNow: string | null;
  opensAt: string | null;
  closesAt: string | null;
}

/** The figures the requester saw when asking (loose: each event records its own). */
export interface PackFactsAtPark {
  scheduled?: boolean;
  closeApplications?: boolean;
  confirmQuizAtRisk?: boolean;
  draftsAffected?: number;
  quizAtRisk?: number;
  byState?: Record<string, number>;
}

/** Live figures for opening a round. */
export interface RoundOpenFigures {
  event: 'stage.open';
  /** Entries the opening would admit now; null when the round is not in the pipeline. */
  admissions: number | null;
  closeApplications: boolean;
  /** Null unless opening screening closes the application window. */
  draftsAffected: number | null;
  /** Null outside screening. */
  quizAtRisk: number | null;
  /** The count the requester confirmed; null when none was needed. */
  quizAtRiskConfirmed: number | null;
  atPark: PackFactsAtPark;
}

/** Live figures for closing a round. */
export interface RoundCloseFigures {
  event: 'stage.close';
  /** The round's entries by state, now. */
  byState: Record<string, number>;
  /** SCREENING only; null otherwise. */
  waiting: number | null;
  returned: number | null;
  atPark: PackFactsAtPark;
}

/** stage.open / stage.close: the round, live figures and what approving does. */
export interface RoundDecisionPack extends DecisionPackBase {
  kind: 'ROUND';
  event: 'stage.open' | 'stage.close';
  round: PackRound;
  figures: RoundOpenFigures | RoundCloseFigures;
  consequences: PackConsequence[];
}

/** One application in an ENTRIES pack, whatever the verdict. */
interface PackEntryBase {
  applicationId: string;
  /** Null when the profile is gone — never a placeholder. */
  applicantName: string | null;
  /** The application's state now; null if it no longer exists. */
  statusNow: string | null;
  /** Its state when the request was made. */
  fromState: string;
  roundName: PackStageName | null;
  outcome: EntryOutcome;
  /** Why the entry could not be applied (STALE only). */
  staleReason: string | null;
}

export interface PackChecklistAnswer {
  criterionId: string;
  /** Null if the item is no longer in the checklist. */
  name: PackStageName | null;
  met: boolean;
  comment: string | null;
}

export interface PassPackEntry extends PackEntryBase {
  checklist: PackChecklistAnswer[];
  nextStageId: string | null;
  nextStageName: PackStageName | null;
}

export interface DisqualifyPackEntry extends PackEntryBase {
  /** Verbatim: exactly what the applicant will read. */
  justification: string;
  justificationSource: 'TYPED' | 'GENERATED';
}

export interface PassEntriesPack extends DecisionPackBase {
  kind: 'ENTRIES';
  event: 'screening.pass';
  entries: PassPackEntry[];
}

export interface DisqualifyEntriesPack extends DecisionPackBase {
  kind: 'ENTRIES';
  event: 'application.disqualify';
  entries: DisqualifyPackEntry[];
}

/**
 * GET /approvals/:instanceId/pack — what is actually being approved, shaped by
 * its event (#46 spec §9).
 *
 * Served by workflow, which owns the parked decision; approvals is domain-blind
 * and holds only the plan and an opaque `packRef`. Without this an approver saw
 * an award id and an event name and nothing else.
 */
export type DecisionPack = DecisionDecisionPack | RoundDecisionPack | PassEntriesPack | DisqualifyEntriesPack;

/**
 * GET /approvals/pack-summaries — one queue row's header (R1): the same values
 * the full pack carries, plus the two figures a row title needs. No entries,
 * figures or applications: the selected item still reads its full pack.
 */
export interface DecisionPackSummary {
  instanceId: string;
  kind: DecisionPackKind;
  awardId: string;
  awardName: string | null;
  event: string;
  stageId: string | null;
  stageName: PackStageName | null;
  status: DecisionPackStatus;
  requestedBySchedule: boolean;
  requestedBy: string | null;
  requestedById: string;
  requestedAt: string;
  releasing: boolean;
  /** STALE only: why the approved request could not be applied; null otherwise. */
  staleReason: string | null;
  /** STALE only: the request named one entry and it has since been withdrawn. */
  withdrawn: boolean;
  /** ENTRIES only: how many entries the request names; null otherwise. */
  entryCount: number | null;
  /** screening.pass only: the round the pass admits into; null otherwise. */
  nextStageName: PackStageName | null;
}

export type Decision = 'APPROVE' | 'REJECT' | 'CHANGES';

/** GET /approvals/delegations/mine — one handoff, from my point of view. */
export interface Delegation {
  id: string;
  gateId: string;
  awardId: string;
  event: string;
  stageId: string | null;
  fromUserId: string;
  toUserId: string;
  /** GIVEN — I handed mine over; RECEIVED — someone handed me theirs. */
  direction: 'GIVEN' | 'RECEIVED';
  startsAt: string;
  endsAt: string;
  /**
   * When the delegator withdrew it early; null while it stands. A separate fact
   * from the range, not a shortened one — the record still says what was handed
   * over, and until when it was meant to run.
   */
  revokedAt: string | null;
  /** Derived by the server from the range and the revocation, never stored. */
  active: boolean;
}

export interface DelegateInput {
  gateId: string;
  toUserId: string;
  startsAt: string;
  endsAt: string;
}
